/**
 * Pantalla de sin conexión.
 *
 * ADR-006: GU requiere internet. Esta pantalla es la ÚNICA red de
 * protección del usuario — no hay borradores locales ni lectura degradada,
 * así que si esto falla o llega tarde, el usuario pierde trabajo.
 *
 * Requisitos que cumple:
 * - Bloquea ANTES, no después. Nadie teclea 300 caracteres para descubrir
 *   que se van a perder.
 * - Explica el porqué en el tono del producto, no con un error técnico.
 * - Nunca culpa al usuario.
 * - Distingue "sin datos" de "servidor caído": son problemas distintos y
 *   el usuario no puede hacer lo mismo ante cada uno.
 */

export type ConnectionProblem = 'no_network' | 'server_unreachable';

export interface OfflineScreenProps {
  problem: ConnectionProblem;
  onRetry: () => void;
  /** true mientras se reintenta, para no permitir toques repetidos. */
  retrying?: boolean;
}

const COPY: Record<
  ConnectionProblem,
  { titulo: string; cuerpo: string; accion: string }
> = {
  no_network: {
    titulo: 'Gourmet Urbano necesita internet',
    cuerpo:
      'El mapa, las Colecciones y la prueba de visita viven en la nube. Conéctate a datos o WiFi y seguimos justo donde estabas.',
    accion: 'Ya me conecté',
  },
  server_unreachable: {
    // Distinto problema, distinta acción: aquí conectarse no arregla nada.
    titulo: 'No podemos alcanzar el servidor',
    cuerpo:
      'Tu conexión está bien, el problema es nuestro. Estamos en ello — vuelve a intentar en un momento.',
    accion: 'Reintentar',
  },
};

export function OfflineScreen({
  problem,
  onRetry,
  retrying = false,
}: OfflineScreenProps) {
  const { titulo, cuerpo, accion } = COPY[problem];

  return (
    <div
      role="alert"
      aria-live="assertive"
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--gu-space-5)',
        padding: 'var(--gu-space-8) var(--gu-space-5)',
        background: 'var(--gu-surface-app)',
        textAlign: 'center',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          fontSize: '2.5rem',
          lineHeight: 1,
          opacity: 0.75,
        }}
      >
        🌮
      </div>

      <div style={{ maxWidth: '26rem' }}>
        <h1
          style={{
            fontSize: 'var(--gu-text-h1)',
            lineHeight: 'var(--gu-leading-tight)',
            margin: `0 0 var(--gu-space-3)`,
          }}
        >
          {titulo}
        </h1>
        <p
          style={{
            margin: 0,
            color: 'var(--gu-text-secondary)',
            lineHeight: 'var(--gu-leading-normal)',
          }}
        >
          {cuerpo}
        </p>
      </div>

      <button
        type="button"
        onClick={onRetry}
        disabled={retrying}
        style={{
          minHeight: 'var(--gu-touch-comfortable)',
          padding: '0 var(--gu-space-6)',
          borderRadius: 'var(--gu-radius-md)',
          // --gu-primary-strong, no --gu-primary: blanco encima solo pasa
          // WCAG AA con esta variante (6.14:1 vs 4.28:1).
          background: 'var(--gu-primary-strong)',
          color: 'var(--gu-white)',
          border: 'none',
          fontSize: 'var(--gu-text-body)',
          fontWeight: 550,
          cursor: retrying ? 'default' : 'pointer',
          opacity: retrying ? 0.7 : 1,
        }}
      >
        {retrying ? 'Buscando señal…' : accion}
      </button>

      <p
        style={{
          margin: 0,
          fontSize: 'var(--gu-text-caption)',
          color: 'var(--gu-text-tertiary)',
          maxWidth: '22rem',
        }}
      >
        Gourmet Urbano también necesita GPS para confirmar que estuviste en
        el lugar.
      </p>
    </div>
  );
}
